import {
  type HistoryOptions,
  parseAsArrayOf,
  parseAsBook,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from '@vrplatform/query-params';
import { day } from '@vrplatform/utils';
import { useNavigate, useParams } from 'react-router';
import { useCallback } from 'react';

export type BalanceSheetTableFilterType = 'month' | 'quarter' | 'year';

export type BalanceSheetTableFilterView = 'non-zero-rows' | 'non-zero-columns';

const VIEWS: BalanceSheetTableFilterView[] = [
  'non-zero-rows',
  'non-zero-columns',
];

export function useBalanceSheetTableFilter() {
  const goto = useNavigate();

  const { type: t } = useParams<{
    type: BalanceSheetTableFilterType;
  }>();

  const type = (t || 'month') as BalanceSheetTableFilterType;

  const defaultDateRange = (() => {
    const today = day();

    if (type === 'month') {
      const lastMonth = today.subtract(1, 'month');
      return `${lastMonth.startOf('month').yyyymmdd()}...${lastMonth.endOf('month').yyyymmdd()}`;
    }

    return `${today.startOf('year').yyyymmdd()}...${today.endOf('year').yyyymmdd()}`;
  })();

  const [filter, setFilter] = useQueryStates({
    date: parseAsString.withDefault(defaultDateRange),
    ledger: parseAsBook,
    view: parseAsArrayOf(parseAsStringLiteral(VIEWS)),
  });

  const [s, e] = filter.date?.split('...') || [];
  const [defaultStartDate, defaultEndDate] = defaultDateRange.split('...');

  const setType = useCallback(
    (value: BalanceSheetTableFilterType) => {
      const search = new URLSearchParams(window.location.search);
      goto(`/reports/balance-sheet/${value}?${search.toString()}`);
    },
    [goto]
  );

  const startDate =
    s && day(s).isValid() ? day(s).yyyymmdd() : defaultStartDate;
  const endDate = e && day(e).isValid() ? day(e).yyyymmdd() : defaultEndDate;

  const setDate = (
    args: { startDate: string; endDate: string },
    history?: HistoryOptions
  ) => setFilter({ date: `${args.startDate}...${args.endDate}` }, { history });

  return {
    filter: {
      ...filter,
      startDate,
      endDate,
      type,
    },
    setDate,
    setType: (updated: BalanceSheetTableFilterType) => {
      const current = type;

      const switchedToYearView = current !== 'year' && updated === 'year';

      if (switchedToYearView) {
        const start = day(startDate).startOf('year').yyyymmdd();
        const end = day(startDate).endOf('year').yyyymmdd();
        setDate({ startDate: start, endDate: end }, 'replace').then(() => {
          setType(updated);
        });
      } else {
        setType(updated);
      }
    },
    setFilter: (args: Partial<Omit<typeof filter, 'date'>> | null) => {
      setFilter(args);
    },
  };
}
